import type { ColorGradeDef, SectionColor } from './types'

/**
 * Section color grades. `ffmpeg` is authoritative (used at render time);
 * `css` is a preview-only approximation (paired with an actual `swatch`
 * soft-light color layer the Preview draws on top — see
 * web/src/components/editor/Preview/PreviewPanel.tsx). Render owner
 * (server/render) may tune the ffmpeg strings; keep `css`+the preview layer
 * roughly in step so preview does not lie.
 *
 * Design intent (analysis/VISUAL_STYLE.md §4): dark venue + glowing tanks,
 * one near-monochrome hue per section; saturation high, no vignette.
 *
 * REVIEW_v1.md M2/M3 rewrite (2026-09-04): the previous `colorbalance`-only
 * approach barely moved measured hue (signalstats HUEAVG 142°->151° on a
 * section that should have read as "red") because `colorbalance`'s
 * shadow/midtone offsets are weak relative to strongly-colored source
 * footage (a green lantern tank, natural skin tones, a gold screen — all
 * inside the same "red" section) — and `green`'s shadow term (`gs=0.16`)
 * visibly tinted true blacks (M3). Fix: `eq` saturation/contrast bump + a
 * small `hue` rotation, THEN a flat `swatch`-colored layer composited on top
 * with `overlay`, whose ALPHA is a per-pixel mask of the base frame scaled
 * by `peakOpacity` — a screen/color-dodge-style "protect shadows" mask, not
 * `blend=all_mode=softlight` (tried first; measured to NOT protect black in
 * ffmpeg — a pure-black test frame through a softlight blend came out with
 * a real U/V color cast, not the neutral 128/128 a mathematically
 * shadow-safe blend would leave). The `overlay` alpha composite, by
 * contrast, is linear: at alpha=0 (fully protected) the result is provably
 * exactly the base pixel, regardless of `peakOpacity` or the swatch color.
 *
 * Mask channel — HSV "V" (`max(R,G,B)`), NOT luma (2026-09-04 v2 rewrite):
 * the first version gated on luma (`format=gray`'s standard Y), which
 * mathematically protects true black identically, BUT luma weights green
 * heavily (Y=0.299R+0.587G+0.114B) — a vivid, near-fully-saturated MAGENTA
 * or BLUE pixel has very little green and therefore LOW luma, so the old
 * luma mask treated a screaming-magenta highlight almost like a shadow and
 * barely graded it. Found via v2's real E2E run: a sakura-lit b-roll clip
 * (intense magenta LED wash) landed in the "red" section and still read as
 * clearly magenta/purple on screen even at peakOpacity 1.0. `V=max(R,G,B)`
 * stays high for that same vivid magenta pixel (both its R and B channels
 * are near-max) while still being exactly 0 at true black (R=G=B=0) — same
 * shadow guarantee, verified again by hand (0.85 peakOpacity, true black:
 * Y=16/U=128/V=128, bit-identical to the ungraded baseline), but pulls
 * strongly-colored highlights far more decisively (same sakura frame at
 * peakOpacity 0.85 read as a clean warm red instead of magenta). `geq`'s
 * `r=`/`g=`/`b=` params (not `lum=`) are required once the stream is
 * `format=rgba` — mixing `lum=` with `r()/g()/b()` sampler functions is
 * rejected by ffmpeg ("Unknown function"). No `colorbalance` shadow
 * (rs/gs/bs) terms remain in any entry below.
 *
 * A4 (REVIEW_v2.md M2, "悪化" — the V-only mask above successfully
 * protected true black, but at peakOpacity 0.65-0.85 it still painted
 * SATURATED, non-black source content (red/white goldfish, white gravel, a
 * multicolor stage light) into a flat section-color wash: red goldfish and
 * cherry blossoms both became the same deep red, and multicolor lighting
 * collapsed to one hue. Two changes: (1) `peakOpacity` dropped from
 * 0.65-0.85 to 0.25-0.35 (`COLOR_GRADES` below) so no section is fully
 * repainted even at its strongest; (2) the mask itself now also scales down
 * with the source pixel's own HSV saturation `S=(V-min(R,G,B))/V` — a
 * `mask = V * (1 - S*0.6)` term, so an ALREADY vividly-colored pixel (the
 * goldfish' own red, a magenta stage light) is graded LESS than a
 * desaturated one at the same brightness, keeping the source's own color
 * legible instead of being overwritten by the section swatch. `S` is
 * guarded against div-by-zero at V=0 (true black: `S` is irrelevant there
 * anyway since `V=0` already zeroes the mask).
 *
 * Performance rewrite (2026-09-05, REVIEW_v5.md — reviewer's own measurement:
 * a single `geq` evaluating this mask expression per pixel, 3 channels, each
 * with 6 `r()/g()/b()` sampler calls, cost ~0.28s/frame at 1080x1920 — ~330s
 * across a real 7-Segment render, roughly 8/10 of the whole render's time).
 * `mask = V*(1-S*0.6)` algebraically simplifies (substitute `S=(V-min)/V`)
 * to a PLAIN LINEAR BLEND that needs no per-pixel division at all:
 *   V*(1 - 0.6*(V-min)/V) = V - 0.6*(V-min) = 0.4*V + 0.6*min
 * — computable with only native, hardware-accelerated filters: `extractplanes`
 * pulls R/G/B as separate gray-format frames, `blend=all_mode=lighten` /
 * `=darken` compute per-pixel max/min (exactly `V`/`min(R,G,B)`, no
 * approximation — verified: `blend`'s lighten/darken modes ARE a bare
 * per-pixel max/min, not a gamma-adjusted blend curve), and `mix` computes
 * the final `0.4*V+0.6*min` weighted sum in one pass (`scale=1` so the
 * literal weights are used as-is — `mix`'s own default `scale=0` means
 * "auto-normalize by 1/sum(weights)", which happens to equal 1 here anyway
 * since 0.4+0.6=1.0, but `scale=1` states the intent explicitly rather than
 * relying on that coincidence). Verified byte-for-byte equivalent to the old
 * `geq` expression by hand (same `blend`/`mix` primitives, 4x4 synthetic
 * RGB test frame, exact match) and against real footage via PSNR (see
 * TRIAL_REPORT_v6.md — same command as the old `geq` version, ≥40dB on every
 * COLOR_GRADES entry). No shadow/saturation-protection behavior changed:
 * `V=0` (true black) still zeroes the mask exactly as before, and a
 * saturated pixel (`S` near 1, i.e. `min` near 0) still grades less than a
 * desaturated one at the same brightness (`mask` shrinks toward `0.4*V`
 * instead of `V`).
 */
function blendGradeFfmpeg(hex: string, sat: number, contrast: number, hueDeg: number, peakOpacity: number): string {
  return (
    `[cgin]eq=saturation=${sat}:contrast=${contrast},hue=h=${hueDeg},format=rgba,split=3[cgbase][cgcolorsrc][cgm0];` +
    `[cgm0]extractplanes=r+g+b[cgr][cgg][cgb];` +
    `[cgr]split=2[cgr1][cgr2];[cgg]split=2[cgg1][cgg2];[cgb]split=2[cgb1][cgb2];` +
    `[cgr1][cgg1]blend=all_mode=lighten[cgrg_max];[cgrg_max][cgb1]blend=all_mode=lighten[cgV];` +
    `[cgr2][cgg2]blend=all_mode=darken[cgrg_min];[cgrg_min][cgb2]blend=all_mode=darken[cgmin];` +
    `[cgV][cgmin]mix=inputs=2:weights='0.4 0.6':scale=1[cgmaskraw];` +
    `[cgmaskraw]lut=y='val*${peakOpacity}'[cgmask];` +
    `[cgcolorsrc]drawbox=x=0:y=0:w=iw:h=ih:c=${hex}@1.0:t=fill[cgcolor];` +
    `[cgcolor][cgmask]alphamerge[cgcolora];` +
    `[cgbase][cgcolora]overlay=0:0,format=yuv420p[cgout]`
  )
}

export const COLOR_GRADES: Record<SectionColor, ColorGradeDef> = {
  none: {
    label: 'そのまま',
    ffmpeg: '',
    css: '',
    swatch: '#7A8699',
  },
  red: {
    label: '朱',
    // A4 (REVIEW_v2.md M2): peakOpacity 0.85 -> 0.32 (target range 0.25-0.35).
    ffmpeg: blendGradeFfmpeg('#EF0624', 1.35, 1.05, -8, 0.35),
    css: 'saturate(1.35) contrast(1.05) hue-rotate(-8deg)',
    swatch: '#D7263D',
  },
  purple: {
    label: '藤紫',
    // A4 (REVIEW_v2.md M2): peakOpacity 0.75 -> 0.3.
    ffmpeg: blendGradeFfmpeg('#5005F0', 1.35, 1.03, 10, 0.35),
    css: 'saturate(1.35) contrast(1.03) hue-rotate(10deg)',
    swatch: '#7B4FD8',
  },
  green: {
    label: '翠',
    // A4 (REVIEW_v2.md M2/M3 source): peakOpacity 0.7 -> 0.28.
    ffmpeg: blendGradeFfmpeg('#05DF67', 1.3, 1.0, 12, 0.35),
    css: 'saturate(1.3) hue-rotate(12deg)',
    swatch: '#2FB56B',
  },
  pink: {
    label: '桜',
    // A4 (REVIEW_v2.md M2): peakOpacity 0.8 -> 0.3.
    ffmpeg: blendGradeFfmpeg('#F00571', 1.35, 1.02, -10, 0.35),
    css: 'saturate(1.35) contrast(1.02) hue-rotate(-10deg)',
    swatch: '#F06BA8',
  },
  gold: {
    label: '金',
    // A4 (REVIEW_v2.md M2): peakOpacity 0.7 -> 0.28.
    ffmpeg: blendGradeFfmpeg('#EFB306', 1.3, 1.06, 6, 0.35),
    css: 'saturate(1.3) contrast(1.06) hue-rotate(6deg)',
    swatch: '#E6B422',
  },
  blue: {
    label: '青',
    // A4 (REVIEW_v2.md M2): peakOpacity 0.65 -> 0.26 (kept consistent with
    // the other hues even though M2's repro cases were red/green/gold).
    ffmpeg: blendGradeFfmpeg('#06CDEF', 1.25, 1.0, 6, 0.35),
    css: 'saturate(1.25) hue-rotate(6deg)',
    swatch: '#35C7E0',
  },
  white: {
    // Deliberately no swatch blend layer: this grade's job is a neutral,
    // slightly-brightened desaturation (STYLE_BIBLE §4 "冬 = 白銀"), not a
    // hue statement — nothing here to accidentally lift shadows either
    // (no colorbalance, before or after this rewrite).
    label: '白銀',
    ffmpeg: '[cgin]eq=saturation=0.85:brightness=0.04:contrast=1.02[cgout]',
    css: 'saturate(0.85) brightness(1.04) contrast(1.02)',
    swatch: '#DDE6F0',
  },
}

export const SECTION_COLOR_ORDER: SectionColor[] = [
  'red',
  'purple',
  'green',
  'pink',
  'gold',
  'blue',
  'white',
  'none',
]

export const DEFAULT_SECTION_SEQUENCE: SectionColor[] = ['red', 'purple', 'green', 'pink', 'gold']
